import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Grid, Chip, CircularProgress } from "@mui/material";
import { makeStyles } from "@material-ui/styles";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import SyncIcon from "@material-ui/icons/Sync";
import SvgIcon from "@material-ui/core/SvgIcon";
import {
  CustomTable,
  ModalError,
  ModalSuccess,
  PrimaryButton,
  SecondaryButton,
  SearchTextInput,
} from "components";
import Swal from "sweetalert2";
import { getErrors } from "utils";
import {
  listSFTPFiles,
  syncSFTP,
  downloadFiles,
  getProcessStatus,
  retryDownload,
} from "utils/DSPSFTPService";

const POLL_INTERVAL_MS = 3000;
const TABLE_MAX_HEIGHT = 460;

const useStyles = makeStyles(() => ({
  folderIcon: { marginRight: 8, color: "#9e9e9e" },
  fileIcon: { marginRight: 8, color: "#9e9e9e" },
  pathText: { fontFamily: "monospace", fontSize: 13 },
  statusChip: { fontSize: 11 },
  folderClickable: {
    cursor: "pointer",
    "&:hover": { color: "#1976d2" },
  },
  progressLabel: { fontSize: 7, fontWeight: "bold" },
  queueChip: { height: 20, fontSize: 10, marginLeft: 8 },
  successText: { color: "#4caf50", fontSize: 13 },
  successIcon: { color: "#4caf50", fontSize: 18 },
  failedText: { color: "#f44336", fontSize: 13 },
  failedIcon: { color: "#f44336", fontSize: 18 },
  sizeText: {
    color: "#9e9e9e",
    fontSize: 10,
    marginLeft: 8,
    whiteSpace: "nowrap",
  },
  speedText: {
    color: "#1976d2",
    fontSize: 10,
    marginTop: 4,
    whiteSpace: "nowrap",
  },
}));

const FolderUpIcon = props => (
  <SvgIcon {...props}>
    <path d="M20 6H12L10 4H4C2.89 4 2 4.89 2 6V18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V8C22 6.89 21.11 6 20 6ZM20 18H4V8H20V18ZM13 13V17H11V13H8L12 9L16 13H13Z" />
  </SvgIcon>
);

const CircularProgressWithLabel = ({ value, ...props }) => {
  const classes = useStyles();
  const numericValue =
    typeof value === "number" ? value : parseFloat(value || 0);
  const displayLabel =
    numericValue < 10 && numericValue > 0 && !Number.isInteger(numericValue)
      ? `${numericValue.toFixed(1)}%`
      : `${Math.round(numericValue)}%`;

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      ml={1}
    >
      <CircularProgress
        variant="determinate"
        size={24}
        value={numericValue}
        {...props}
      />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
          className={classes.progressLabel}
        >
          {displayLabel}
        </Typography>
      </Box>
    </Box>
  );
};

/** Format file size to human-readable string */
const formatFileSize = sizeStr => {
  if (!sizeStr && sizeStr !== 0) return "";
  if (typeof sizeStr === "string" && /[a-zA-Z]/.test(sizeStr)) return sizeStr;
  const bytes = typeof sizeStr === "string" ? parseInt(sizeStr, 10) : sizeStr;
  if (isNaN(bytes) || bytes < 0) return String(sizeStr);
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${
    value < 10 && i > 0
      ? value.toFixed(2)
      : value < 100 && i > 0
      ? value.toFixed(1)
      : Math.round(value)
  } ${units[i]}`;
};

/** Format last synced time — hours when > 60 minutes */
const formatLastSynced = lastSynced => {
  if (!lastSynced) return "Not synced yet";
  const diffMs = Date.now() - lastSynced.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Last synced just now";
  if (diffMin < 60) return `Last synced ${diffMin} min ago`;
  const diffHours = (diffMin / 60).toFixed(1);
  return `Last synced ${diffHours} hours ago`;
};

function SFTPExplorer({ dsp, onDownloadStarted }) {
  const classes = useStyles();

  const [currentPath, setCurrentPath] = useState("");
  const [pathHistory, setPathHistory] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [processId, setProcessId] = useState(null);
  const [processStatus, setProcessStatus] = useState(null);

  const dspSlug = dsp?.slug || dsp?.label?.toLowerCase()?.replace(/\s+/g, "-");

  // ─── Fetch remote SFTP listing ──────────────────────────────────────────────
  const fetchFiles = useCallback(
    async (path = "", search = "", isBackground = false) => {
      if (!dspSlug) return;
      if (!isBackground) {
        setIsLoading(true);
        setSelectedItems([]);
      }
      try {
        const res = await listSFTPFiles(dspSlug, path, search);
        const payload = res?.data?.data || res?.data || {};

        // backend typo on_progres
        setIsSyncing(payload.sync_status === "on_progres");

        // Extract files array
        const filesArray = Array.isArray(payload)
          ? payload
          : payload.files || [];
        setFiles(filesArray);

        // Update last synced if provided
        if (payload.last_sync) {
          setLastSynced(new Date(payload.last_sync));
        }
      } catch (error) {
        if (!isBackground) ModalError(getErrors(error?.response));
      } finally {
        if (!isBackground) setIsLoading(false);
      }
    },
    [dspSlug]
  );

  useEffect(() => {
    if (dspSlug) {
      setCurrentPath("");
      setPathHistory([]);
      setSearchQuery("");
      fetchFiles("", "");
    }
  }, [dspSlug, fetchFiles]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const handler = setTimeout(() => {
      if (dspSlug) {
        fetchFiles(currentPath, searchQuery);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // ─── Poll sync status ───────────────────────────────────────────────────────
  useEffect(() => {
    let interval;
    if (isSyncing) {
      interval = setInterval(() => {
        fetchFiles(currentPath, searchQuery, true);
      }, 60000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isSyncing, currentPath, searchQuery, fetchFiles]);

  // ─── Poll process status ────────────────────────────────────────────────────
  useEffect(() => {
    if (!processId || processStatus === "failed") return; // Stop polling if failed or no processId
    const interval = setInterval(async () => {
      try {
        const res = await getProcessStatus(processId);
        const status = res?.data?.data?.status;
        setProcessStatus(status);
        if (
          status === "done" ||
          status === "failed" ||
          status === "completed"
        ) {
          clearInterval(interval);
          if (status === "failed") {
            ModalError("Download process failed. Please retry.");
            // Do NOT clear processId so we can retry it
          } else {
            setProcessId(null);
            ModalSuccess("Download completed successfully!");
            fetchFiles(currentPath, searchQuery);
            if (onDownloadStarted) onDownloadStarted();
          }
        }
      } catch {
        clearInterval(interval);
        setProcessId(null);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [processId, processStatus, currentPath, fetchFiles, onDownloadStarted]);

  // ─── Selection helpers ──────────────────────────────────────────────────────
  const handleSelect = (event, option) => {
    const { checked } = event?.target || {};
    setSelectedItems(prev =>
      checked
        ? [...prev, option]
        : prev.filter(
            item => (item.id || item.name) !== (option.id || option.name)
          )
    );
  };

  // ─── Navigation ─────────────────────────────────────────────────────────────
  const handleNavigateInto = item => {
    if (!item.is_dir && item.type !== "folder" && item.type !== "directory")
      return;
    const newPath =
      item.path ||
      (currentPath === "" ? item.name : `${currentPath}/${item.name}`);
    setPathHistory(prev => [...prev, currentPath]);
    setCurrentPath(newPath);
    setSearchQuery("");
    fetchFiles(newPath, "");
  };

  const handleNavigateBack = () => {
    const prev = pathHistory[pathHistory.length - 1] ?? "";
    setPathHistory(h => h.slice(0, -1));
    setCurrentPath(prev);
    setSearchQuery("");
    fetchFiles(prev, "");
  };

  const handleSync = async () => {
    if (!dspSlug) return;
    try {
      await syncSFTP(dspSlug);
      setLastSynced(new Date());
      await fetchFiles(currentPath, searchQuery);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  // ─── Download ───────────────────────────────────────────────────────────────
  const handleDownload = async (overrideExist = false) => {
    if (!selectedItems.length) return;
    const filePaths = selectedItems.map(item => ({
      remote_path:
        item.path ||
        (currentPath === "" ? item.name : `${currentPath}/${item.name}`),
    }));
    setIsDownloading(true);
    try {
      const payload = {
        dsp_slug: dspSlug,
        files: filePaths,
      };
      if (typeof overrideExist === "boolean" && overrideExist) {
        payload.override_exist = true;
      }
      const res = await downloadFiles(payload);
      const pid = res?.data?.data?.process_id;
      if (pid) {
        setProcessId(pid);
        setProcessStatus("running");
        Swal.fire({
          icon: "info",
          title: "Download Started",
          text: `Process ID: ${pid}. Downloading in background…`,
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        ModalSuccess(res?.data?.message || "Download started!");
      }
      setSelectedItems([]);
    } catch (error) {
      const resData = error?.response?.data;
      const errorCode =
        resData?.errors?.code ||
        resData?.code ||
        resData?.error?.code ||
        (Array.isArray(resData?.errors) ? resData?.errors[0]?.code : null) ||
        resData?.error_code;

      if (Number(errorCode) === 1336) {
        const confirmResult = await Swal.fire({
          icon: "warning",
          title: "File Already Exists",
          text: "Selected file(s) already exist. Do you want to replace them?",
          showCancelButton: true,
          confirmButtonText: "Yes, Replace",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        });

        if (confirmResult.isConfirmed) {
          await handleDownload(true);
          return;
        }
      } else {
        ModalError(getErrors(error?.response));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // ─── Retry Download ─────────────────────────────────────────────────────────
  const handleRetryDownload = async () => {
    if (!processId) return;
    setIsRetrying(true);
    try {
      await retryDownload({ process_id: processId });
      setProcessStatus("running");
      Swal.fire({
        icon: "info",
        title: "Retry Started",
        text: `Retrying process ID: ${processId}…`,
        timer: 3000,
        showConfirmButton: false,
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setIsRetrying(false);
    }
  };

  // ─── Columns ─────────────────────────────────────────────────────────────────
  const columnTable = [
    {
      name: "all",
      title: (
        <Box display="flex" alignItems="center" gap={1}>
          <span className={classes.pathText}>
            {currentPath === "" ? "/" : `/${currentPath}`}
          </span>
          {processStatus &&
            processStatus !== "done" &&
            processStatus !== "completed" && (
              <Chip
                label={processStatus}
                size="small"
                color={processStatus === "failed" ? "error" : "info"}
                className={classes.statusChip}
              />
            )}
        </Box>
      ),
      renderText: item => {
        if (!item || typeof item !== "object") return null;
        if (item.is_back_button) {
          return (
            <Box
              display="flex"
              alignItems="center"
              className={classes.folderClickable}
              onClick={() => handleNavigateBack()}
            >
              <FolderUpIcon className={classes.folderIcon} />
              ...
            </Box>
          );
        }
        const isFolder =
          item.is_dir || item.type === "folder" || item.type === "directory";
        const status = item.progress_flag
          ? item.download_status
          : item.download_status ||
            (item.status === "success"
              ? "F"
              : item.status === "failed"
              ? "E"
              : null);

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box
              display="flex"
              alignItems="center"
              className={isFolder ? classes.folderClickable : ""}
              onClick={() => isFolder && handleNavigateInto(item)}
            >
              {isFolder ? (
                <FolderOutlinedIcon className={classes.folderIcon} />
              ) : (
                <InsertDriveFileOutlinedIcon className={classes.fileIcon} />
              )}
              <Box display="flex" flexDirection="column">
                <Box display="flex" alignItems="center">
                  {item.name}
                  {/* File size */}
                  {item.size != null && (
                    <Typography component="span" className={classes.sizeText}>
                      {" "}
                      ({formatFileSize(item.size)})
                    </Typography>
                  )}
                </Box>
                {/* Download Speed */}
                {status === "R" && item.download_speed && (
                  <Typography
                    variant="caption"
                    component="div"
                    className={classes.speedText}
                  >
                    {item.download_speed}
                  </Typography>
                )}
              </Box>

              {status === "R" && (
                <Box display="flex" alignItems="center" ml={2}>
                  <CircularProgressWithLabel value={item.download_progress} />
                </Box>
              )}

              {status === "Q" && (
                <Chip
                  label="Queue"
                  size="small"
                  variant="outlined"
                  color="info"
                  className={classes.queueChip}
                />
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
              {status === "F" && (
                <>
                  <Typography
                    variant="body2"
                    fontStyle="italic"
                    className={classes.successText}
                  >
                    Download Success
                  </Typography>
                  <CheckIcon className={classes.successIcon} />
                </>
              )}

              {status === "E" && (
                <>
                  <Typography
                    variant="body2"
                    fontStyle="italic"
                    className={classes.failedText}
                  >
                    Download Failed
                  </Typography>
                  <CloseIcon className={classes.failedIcon} />
                </>
              )}
            </Box>
          </Box>
        );
      },
    },
  ];

  const allData =
    pathHistory.length > 0
      ? [{ is_back_button: true, name: "...", is_dir: true }, ...files]
      : files;
  const filteredData = searchQuery
    ? allData.filter(
        item =>
          item.is_back_button ||
          item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allData;

  const selectableFiles = filteredData.filter(
    item =>
      !item?.is_back_button &&
      !item?.is_dir &&
      item?.type !== "folder" &&
      item?.type !== "directory"
  );

  const allSelected =
    selectableFiles.length > 0 &&
    selectableFiles.every(item =>
      selectedItems.some(s => (s.id || s.name) === (item.id || item.name))
    );

  const handleSelectAll = event => {
    const { checked } = event?.target || {};
    if (checked) {
      setSelectedItems(prev => {
        const newItems = selectableFiles.filter(
          item =>
            !prev.some(s => (s.id || s.name) === (item.id || item.name))
        );
        return [...prev, ...newItems];
      });
    } else {
      setSelectedItems(prev =>
        prev.filter(
          s =>
            !selectableFiles.some(
              item => (item.id || item.name) === (s.id || s.name)
            )
        )
      );
    }
  };

  return (
    <Box>
      <Grid container justifyContent="space-between" alignItems="center" mb={1}>
        <Grid item display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight="bold">
            SFTP DSP
          </Typography>
        </Grid>
        <Grid item display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="textSecondary">
            {formatLastSynced(lastSynced)}
          </Typography>
          <SecondaryButton
            label={isSyncing ? "Syncing…" : "Sync"}
            onClick={handleSync}
            disabled={isSyncing || !dspSlug}
            startIcon={
              isSyncing ? <CircularProgress size={14} /> : <SyncIcon />
            }
            size="small"
          />
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Box mb={1} width="100%">
        <SearchTextInput
          placeholder="Search files..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          clear={true}
          onClear={() => setSearchQuery("")}
          size="small"
          width="100%"
        />
      </Box>

      <CustomTable
        isLoading={isLoading}
        columnTable={columnTable}
        data={filteredData}
        maxHeight={TABLE_MAX_HEIGHT}
        isPagination={false}
        isHaveCheckbox={true}
        checkboxHeaderChecked={allSelected}
        disabledCheckboxHeaderChecked={selectableFiles.length === 0}
        handleCheckboxHeader={handleSelectAll}
        hiddenCheckbox={item =>
          item?.is_back_button ||
          item?.is_dir ||
          item?.type === "folder" ||
          item?.type === "directory"
        }
        checked={item =>
          selectedItems.some(s => (s.id || s.name) === (item.id || item.name))
        }
        handleCheckbox={handleSelect}
      />

      <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
        {processStatus === "failed" && (
          <SecondaryButton
            label={isRetrying ? "Retrying…" : "Retry Download"}
            onClick={handleRetryDownload}
            disabled={isRetrying}
            startIcon={
              isRetrying ? <CircularProgress size={14} color="inherit" /> : null
            }
          />
        )}
        <PrimaryButton
          label={
            isDownloading
              ? "Downloading…"
              : processId && processStatus !== "failed"
              ? "Processing…"
              : `Download to Gluster (${selectedItems.length})`
          }
          onClick={() => handleDownload(false)}
          disabled={
            selectedItems.length === 0 ||
            isDownloading ||
            (!!processId && processStatus !== "failed")
          }
          startIcon={
            isDownloading || (!!processId && processStatus !== "failed") ? (
              <CircularProgress size={14} color="inherit" />
            ) : null
          }
        />
      </Box>
    </Box>
  );
}

export default SFTPExplorer;
